import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { StyleClassModule } from 'primeng/styleclass';
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LanguageService } from '../../services/language.service';
import { AuthenticationService } from '../../services/permission/authentication.service';
import { LocalStorageService } from '../../services/storage/local-storage.service';
import { HardCodedConfigService, LanguageOption } from '../../services/util/hard-coded-config.service';
import { LayoutService } from '../service/layout.service';
import { AppConfigurator } from './app.configurator';

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [RouterModule, CommonModule, StyleClassModule, MenuModule, TranslateModule, AppConfigurator],
  template: ` <div class="layout-topbar">
    <div class="layout-topbar-logo-container">
      <button
        class="layout-menu-button layout-topbar-action"
        (click)="layoutService.onMenuToggle()"
      >
        <i class="pi pi-bars"></i>
      </button>
      <a
        class="layout-topbar-logo"
        routerLink="/"
      >
        <img
          src="assets/images/applogo.svg"
          alt="SmartApart Logo"
          class="h-8 dark:hidden"
        />
        <img
          src="assets/images/applogo-white.svg"
          alt="SmartApart Logo"
          class="hidden h-8 dark:block"
        />
      </a>
    </div>

    <div class="layout-topbar-actions">
      <div class="layout-config-menu">
        <button
          type="button"
          class="layout-topbar-action"
          (click)="toggleDarkMode()"
        >
          <i
            class="pi"
            [ngClass]="layoutService.layoutConfig().darkTheme ? 'pi-sun' : 'pi-moon'"
          ></i>
        </button>

        <div class="relative">
          <button
            type="button"
            class="layout-topbar-action"
            pStyleClass="@next"
            enterFromClass="hidden"
            enterActiveClass="animate-scalein"
            leaveToClass="hidden"
            leaveActiveClass="animate-fadeout"
            [hideOnOutsideClick]="true"
          >
            <i class="pi pi-palette"></i>
          </button>
          <app-configurator class="absolute right-0 top-12 z-50" />
        </div>

        <button
          type="button"
          class="layout-topbar-action flex items-center"
          (click)="langMenu.toggle($event)"
        >
          <img
            [src]="currentFlag"
            alt="Current Language"
            class="h-auto w-8"
          />
        </button>
        <p-menu
          #langMenu
          [model]="languageMenuItems"
          [popup]="true"
        >
          <ng-template
            pTemplate="item"
            let-item
          >
            <div
              class="p-menuitem-content"
              (click)="item.command()"
            >
              <a class="p-menuitem-link flex cursor-pointer items-center p-2">
                <img
                  [src]="item.flag"
                  [alt]="item.label"
                  class="mx-3 h-auto w-6"
                />
                <span class="p-menuitem-text text-base">{{ item.label }}</span>
              </a>
            </div>
          </ng-template>
        </p-menu>
      </div>

      <button
        class="layout-topbar-menu-button layout-topbar-action"
        pStyleClass="@next"
        enterFromClass="hidden"
        enterActiveClass="animate-scalein"
        leaveToClass="hidden"
        leaveActiveClass="animate-fadeout"
        [hideOnOutsideClick]="true"
      >
        <i class="pi pi-ellipsis-v"></i>
      </button>

      <!-- Mobile menu - hidden on large screens, visible on small screens when clicking the ellipsis -->
      <ul class="layout-topbar-menu hidden">
        <li *ngIf="isLoggedIn">
          <button
            type="button"
            class="layout-topbar-action"
            (click)="logout()"
          >
            <i class="pi pi-sign-out"></i>
            <span>{{ 'menu.logout' | translate }}</span>
          </button>
        </li>
      </ul>

      <!-- Desktop menu - visible only on large screens -->
      <div class="layout-topbar-menu hidden lg:block">
        <div
          class="layout-topbar-menu-content"
          *ngIf="isLoggedIn"
        >
          <button
            type="button"
            class="layout-topbar-action"
            (click)="userMenuLg.toggle($event)"
          >
            <i class="pi pi-user"></i>
            <span>{{ username }}</span>
          </button>
          <p-menu
            #userMenuLg
            [model]="userMenuItems"
            [popup]="true"
          ></p-menu>
        </div>
      </div>
    </div>
  </div>`
})
export class AppTopbar {
  toggleConfigSidebar(): void {
    this.layoutService.layoutState.update((state) => ({
      ...state,
      configSidebarVisible: !state.configSidebarVisible
    }));
  }

  userMenuItems: MenuItem[] = [];

  private initializeUserMenu(): void {
    this.userMenuItems = [
      {
        label: this.translate.instant('menu.logout'),
        icon: 'pi pi-fw pi-sign-out',
        command: () => this.logout()
      }
    ];
  }

  languageMenuItems: MenuItem[] = [];

  constructor(
    public layoutService: LayoutService,
    private authService: AuthenticationService,
    private router: Router,
    private translate: TranslateService,
    private hardCodedConfig: HardCodedConfigService,
    private storageClient: LocalStorageService,
    private languageService: LanguageService
  ) {
    // Initialize menu items
    this.initializeLanguageMenu();
    this.initializeUserMenu();

    // Subscribe to language changes to update menu labels
    this.translate.onLangChange.subscribe(() => {
      this.initializeUserMenu();
    });
  }

  private initializeLanguageMenu(): void {
    this.languageMenuItems = this.hardCodedConfig.AVAILABLE_LANGUAGES.map((lang: LanguageOption) => ({
      label: lang.name,
      flag: lang.flag,
      command: () => this.changeLanguage(lang.code)
    }));
  }

  get isLoggedIn(): boolean {
    return this.authService.isUserLoggedIn();
  }

  get username(): string | null {
    return this.authService.getUserName();
  }

  get currentLang(): string {
    return this.languageService.getCurrentLanguage();
  }

  get currentFlag(): string {
    const lang = this.currentLang;
    const foundLang = this.hardCodedConfig.AVAILABLE_LANGUAGES.find((l) => l.code === lang);
    return foundLang?.flag || this.hardCodedConfig.AVAILABLE_LANGUAGES[0].flag;
  }

  logout(): void {
    this.authService.logoutUser();
    this.router.navigate(['/auth/login']);
  }

  toggleDarkMode() {
    this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
  }

  changeLanguage(lang: string): void {
    this.languageService.changeLanguage(lang);
  }
}
